<script setup>
// Facebook.vue — Fase 1.4 do plano .ai/messenger-integration-plan.md:
// substitui o formulário manual (inboxName/facebookId/facebookAccessToken +
// inboxes/createChannel genérico) pelo botão único de OAuth, mesmo padrão do
// Instagram.vue. Cobre também a Fase 1.3 (escolha de Page): quando o
// callback devolve mais de uma Page, redireciona de volta pra esta MESMA
// tela com `selection_token` na query — o componente detecta isso e troca o
// botão de conectar por uma lista simples pra escolher qual Page.
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { toast } from 'dashboard/helper/toast';
import { metaAuthErrorMessage } from 'dashboard/helper/metaAuthError';

import facebookClient from 'dashboard/api/channel/fbChannel';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const isRequestingAuthorization = ref(false);
const isLoadingPages = ref(false);
const isCreatingInbox = ref(false);
const pages = ref([]);

const selectionToken = computed(() => route.query.selection_token);

async function requestAuthorization() {
  try {
    isRequestingAuthorization.value = true;
    const {
      data: { url },
    } = await facebookClient.createAuthorization();

    window.location.href = url;
  } catch (error) {
    toast.error(metaAuthErrorMessage(error, t, 'INBOX_MGMT.ADD.FB'));
  } finally {
    isRequestingAuthorization.value = false;
  }
}

async function fetchPages() {
  try {
    isLoadingPages.value = true;
    const { data } = await facebookClient.fetchPagesForSelection(
      selectionToken.value
    );
    pages.value = data.pages;
  } catch (error) {
    toast.error(metaAuthErrorMessage(error, t, 'INBOX_MGMT.ADD.FB'));
  } finally {
    isLoadingPages.value = false;
  }
}

async function selectPage(page) {
  try {
    isCreatingInbox.value = true;
    const { data } = await facebookClient.finalizeAuthorization({
      selectionToken: selectionToken.value,
      facebookId: page.id,
    });

    router.replace({
      name: 'settings_inbox_finish',
      params: { page: 'new', inbox_id: data.id },
    });
  } catch (error) {
    toast.error(metaAuthErrorMessage(error, t, 'INBOX_MGMT.ADD.FB'));
  } finally {
    isCreatingInbox.value = false;
  }
}

onMounted(() => {
  if (selectionToken.value) {
    fetchPages();
  }
});
</script>

<template>
  <div
    class="border border-n-weak bg-n-background h-full p-6 w-full max-w-full md:w-3/4 md:max-w-[75%] flex-shrink-0 flex-grow-0"
  >
    <div v-if="selectionToken" class="text-center">
      <h3 class="text-n-slate-12 mb-2">
        {{ t('INBOX_MGMT.ADD.FB.SELECT_PAGE.TITLE') }}
      </h3>
      <p class="text-n-slate-11 mb-4">
        {{ t('INBOX_MGMT.ADD.FB.SELECT_PAGE.HELP') }}
      </p>
      <div
        v-if="isLoadingPages"
        class="flex items-center justify-center py-10 text-n-slate-11"
      >
        <Spinner />
      </div>
      <ul v-else class="flex flex-col gap-2 text-left">
        <li v-for="page in pages" :key="page.id">
          <button
            type="button"
            class="w-full flex items-center gap-3 p-3 border border-n-weak rounded-lg hover:bg-n-slate-3 disabled:opacity-50"
            :disabled="isCreatingInbox"
            @click="selectPage(page)"
          >
            <img
              v-if="page.picture"
              :src="page.picture"
              class="w-8 h-8 rounded-full flex-shrink-0"
              alt=""
            />
            <span class="text-n-slate-12">{{ page.name }}</span>
          </button>
        </li>
      </ul>
    </div>
    <div v-else class="text-center">
      <h3 class="text-n-slate-12 mb-2">
        {{ t('INBOX_MGMT.ADD.FB.CONNECT_YOUR_FACEBOOK_PAGE') }}
      </h3>
      <p class="text-n-slate-11 mb-4">
        {{ t('INBOX_MGMT.ADD.FB.HELP') }}
      </p>
      <form @submit.prevent="requestAuthorization">
        <NextButton
          type="submit"
          solid
          blue
          icon="i-ri-facebook-circle-fill"
          :is-loading="isRequestingAuthorization"
          :label="t('INBOX_MGMT.ADD.FB.CONTINUE_WITH_FACEBOOK')"
        />
      </form>
    </div>
  </div>
</template>
